import React, { FC } from 'react';
import {
  BooleanInput,
  NullableBooleanInput,
  Filter,
  ReferenceInput,
  SelectInput,
  DateInput,
} from 'react-admin';
import { DebouncedTextInput } from 'components/DebouncedTextInput';
import Box from '@material-ui/core/Box';
import { AppRole } from 'providers/roles';
import { useStyles } from '../views/master-sheet-styles';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { LazyReferenceInput } from 'components/LazyAutoCompleteInput';
import { SupervisorReferenceInput } from 'components/SupervisorReferenceInput';
import { useWorkflowConfig } from '../config';

export const MasterSheetFilters: FC<any> = props => {
  const config = useWorkflowConfig();
  const { hasRole } = PermissionsProvider.useContainer();

  const isSupervisor = hasRole(AppRole.Supervisor);
  const isAdmin =
    hasRole(AppRole.SuperAdmin) || hasRole(AppRole.RegionalAdmin);

  const isWarranty =
    window.location.href.indexOf(config.edit.warrantyUrlSegment) !== -1;

  const classes = useStyles();
  return (
    <Box>
      <Filter {...props}>
        {!isSupervisor && (
          <SupervisorReferenceInput
            source="supervisorUserIds||$cont"
            alwaysOn
          />
        )}
        <DebouncedTextInput
          label="Lot"
          source="jobLot||$eq"
          debounceTime={800}
          alwaysOn
          resettable
          data-cy="masterSheetFilterLot"
        />
        <DebouncedTextInput
          label="Block"
          source="jobBlock||$eq"
          debounceTime={800}
          alwaysOn
          resettable
          data-cy="masterSheetFilterBlock"
        />
        {isAdmin && !isWarranty && (
          <DebouncedTextInput
            label="PO Id"
            source="poId||$eq"
            debounceTime={800}
            alwaysOn
            resettable
            data-cy="masterSheetV2FilterPoId"
          />
        )}
        <LazyReferenceInput
          label="Builder"
          source="builderId"
          reference="builder"
          resettable
          data-cy="masterSheetFilterBuilder"
          alwaysOn
          allowEmpty={false}
          filterToQuery={value => (value ? { name: value } : {})}
          sort={{ field: 'name', order: 'ASC' }}
          optionText="name"
          optionValue="id"
        />
        <LazyReferenceInput
          label="Subdivision"
          source="subdivisionId"
          reference="subdivision"
          resettable
          data-cy="masterSheetFilterSubdivision"
          alwaysOn
          allowEmpty={false}
          filterToQuery={value => (value ? { name: value } : {})}
          sort={{ field: 'name', order: 'ASC' }}
          optionText="name"
          optionValue="id"
        />
        <ReferenceInput
          label="Phase"
          source="currentPhaseId"
          perPage={Number.MAX_SAFE_INTEGER}
          reference="phase"
          resettable
          alwaysOn
          data-cy="masterSheetFilterPhase"
          allowEmpty={false}
          sort={{ field: 'priority', order: 'ASC' }}
          filter={{
            'phaseStructureType||$eq': config.filters.phaseStructureType,
            ...(isWarranty && config.filters.warrantyPriorityRange
              ? {
                  'priority||$gte': config.filters.warrantyPriorityRange.gte,
                  'priority||$lte': config.filters.warrantyPriorityRange.lte,
                }
              : config.filters.priorityRange
              ? {
                  'priority||$gte': config.filters.priorityRange.gte,
                  'priority||$lte': config.filters.priorityRange.lte,
                }
              : {}),
          }}
        >
          <SelectInput optionText="name" optionValue="id" />
        </ReferenceInput>
        <BooleanInput
          source="currentPhaseIsOnTime||$eq"
          label="Phase On Time"
          data-cy="masterSheetFilterPhaseOnTime"
        />
        <BooleanInput
          source="currentPhaseRequestIsOnTime||$eq"
          label="Request On Time"
          data-cy="masterSheetFilterRequestOnTime"
        />
        <BooleanInput
          source="currentPhaseApprovalIsOnTime||$eq"
          label="Approval On Time"
          data-cy="masterSheetFilterApprovalOnTime"
        />
        <DateInput
          className={classes.filterDate}
          label="Res. Req. On or After"
          source="currentPhaseDateRequested||$gt"
        />
        <DateInput
          className={classes.filterDate}
          label="Res. Req. On or Before"
          source="currentPhaseDateRequested||$lt"
        />
        {!isWarranty && (
          <NullableBooleanInput
            source="activeJob||$eq"
            label="Active Jobs"
            alwaysOn
            options={{
              SelectProps: { displayEmpty: true },
              InputLabelProps: { shrink: true },
            }}
            nullLabel="All"
            falseLabel="Inactive"
            trueLabel="Active"
            data-cy="masterSheetFilterActiveJob"
          />
        )}
        {!isWarranty && (
          <NullableBooleanInput
            source="preRock||$eq"
            label="Pre-Rock"
            alwaysOn
            options={{
              SelectProps: { displayEmpty: true },
              InputLabelProps: { shrink: true },
            }}
            nullLabel="All"
            falseLabel="No"
            trueLabel="Yes"
            data-cy="masterSheetFilterActivesJob"
          />
        )}
        {!isWarranty && (
          <LazyReferenceInput
            label="Contractor"
            // contractorId is not used to filter.
            // Instead, we hook into the request in the dataProvider
            // and apply the correct filters.
            source="contractorId"
            reference="contractor"
            data-cy="masterSheetFilterContractor"
            sort={{ field: 'name', order: 'ASC' }}
            filterToQuery={value => {
              if (value) {
                return {
                  s: {
                    $and: [
                      {
                        name: { $cont: value },
                      },
                    ],
                  },
                };
              }
            }}
            allowEmpty={false}
            resettable
            optionText="name"
            optionValue="id"
            suggestionLimit={25}
          />
        )}
        <DebouncedTextInput
          label="Address"
          source="address1||$cont"
          debounceTime={800}
          alwaysOn
          resettable
          data-cy="address1Filter"
        />
      </Filter>
    </Box>
  );
};

